
import {
  ref,
  reactive,
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
} from "vue";
import _ from "lodash";
import useCompileContent from "@/hooks/useCompileContent";
import { splitImg } from "@/utils/question";
import { unLock } from "@/utils/liner-lock";
import CustomAudio from "@/components/CustomAudio.vue";
import { ElLoading } from "element-plus";

export default defineComponent({
  name: "JigsawQuestion",
  props: {
    question: {
      type: Object,
    },
    disabled: {
      type: Boolean,
    },
    showQuestionNum: {
      type: Boolean,
      default: true,
    },
    showQuestionScore: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    CustomAudio,
  },
  setup(props: any) {
    let loading: any = ref("");
    let jigSawLoading = ref(false);
    let jigSawText = ref("处理中...");
    const sceneRef = ref();
    const initLoading = ref(true);
    const boardRef = ref();
    const { compileQuestionTitle } = useCompileContent();
    const getBoardStyle = (extra = 0) => {
      const x = props.question.question_params.aspect_ratio_length;
      const y = props.question.question_params.aspect_ratio_width;
      if (boardRef.value) {
        const height =
          (boardRef.value.offsetWidth / x) * y + sceneData.board.row * 4;

        return {
          height: `${height + extra}px`,
        };
      }
      return {};
    };
    const getAspectRatioBoardStyle = () => {
      const x = props.question.question_params.aspect_ratio_length;
      const y = props.question.question_params.aspect_ratio_width;
      return {
        aspectRatio: `${x} / ${y}`,
      };
    };
    const getJigsawWidth = () => boardRef.value?.offsetWidth || 500;
    const isPreview = computed(() => props.disabled ?? false);
    const typeMap = {
      option: { value: 0 },
      board: { value: 1 },
    };
    const boardItem = {
      id: null,
    };
    const questionData = ref(props.question || {});
    const answerImgSrc = ref(
      props.question.question_params.images.filter((i: any) => !i.is_fake)[0]
        .url
    );
    // * 标题编译
    const questionTitle = ref<HTMLDivElement>();
    compileQuestionTitle(questionTitle, questionData.value.title || "");
    const initAnswerData: any = ref(props.question.answer_params?.items || []);

    // 场景数据
    const sceneData: any = reactive({
      board: {
        row: props.question.question_params.row_num,
        col: props.question.question_params.col_num,
        aspectRatioLength: props.question.question_params.aspect_ratio_length,
        aspectRatioWidth: props.question.question_params.aspect_ratio_width,
      },
      optionsData: props.question.question_params.image_items || [],
    });
    // 画板数据
    let boardData: any = ref([]);
    const activeItem: any = ref({});

    const answerData: any = computed(() =>
      boardData.value.flat().filter((i: any) => i.id)
    );

    // 移动端拖动img
    const draggingImgData: any = reactive({
      show: false,
      url: "",
    });

    const clearDraggingImg = () => {
      draggingImgData.show = false;
      draggingImgData.style = {};
      draggingImgData.url = "";
    };

    /**
     * 🧩交互
     */
    const changeItem = (x: any, y: any, type: any, item: any = null) => {
      const handleItem: any = item ?? activeItem.value;
      if (
        handleItem.type === typeMap.option.value &&
        type === typeMap.board.value
      ) {
        // 放入
        const targetItem: any = _.cloneDeep(boardData.value[y][x]);
        const newBoardItem =
          sceneData.optionsData.filter(
            (i: any) => i.id === handleItem.dataId
          )[0] || {};
        boardData.value[y][x] = { ...newBoardItem, y, x };
        sceneData.optionsData = sceneData.optionsData.filter(
          (i: any) => i.id !== handleItem.dataId
        );
        if (targetItem?.id) {
          sceneData.optionsData.push(targetItem);
        }
        return;
      }
      if (
        handleItem.type === typeMap.board.value &&
        type === typeMap.board.value
      ) {
        // 板块内移动
        const targetItem = _.cloneDeep(boardData.value[y][x]);
        targetItem.x = handleItem.x;
        targetItem.y = handleItem.y;
        boardData.value[y][x] = _.cloneDeep(
          boardData.value[handleItem.y][handleItem.x]
        );
        boardData.value[y][x].x = x;
        boardData.value[y][x].y = y;
        boardData.value[handleItem.y][handleItem.x] = targetItem;
        return;
      }
      if (
        handleItem.type === typeMap.board.value &&
        type === typeMap.option.value
      ) {
        // 放回
        const sourceItem = _.cloneDeep(
          boardData.value[handleItem.y][handleItem.x]
        );
        sceneData.optionsData.push(sourceItem);
        boardData.value[handleItem.y][handleItem.x] = _.cloneDeep(boardItem);
      }
    };

    const handleDragStart = (element: any) => {
      activeItem.value = {
        dataId: element.getAttribute("dataId"),
        url: element.getAttribute("src"),
        type: parseInt(element.getAttribute("type")),
        x: parseInt(element.getAttribute("dataX") || -1),
        y: parseInt(element.getAttribute("dataY") || -1),
      };
    };

    const handleDrop = (element: any) => {
      const x = parseInt(element.getAttribute("dataX") || -1);
      const y = parseInt(element.getAttribute("dataY") || -1);
      const type = parseInt(element.getAttribute("type"));
      if (
        x >= 0 ||
        (type === typeMap.option.value &&
          activeItem.value.type === typeMap.board.value)
      ) {
        changeItem(x, y, type);
      }
      activeItem.value = {};
    };

    /**
     * 分割图片
     */
    const splitImage = async () => {
      let optionsData: any[] = [];
      for (const image of questionData.value.question_params.images) {
        // 分割image
        const tmpPieces: any = await splitImg(
          image.url,
          sceneData.board.row,
          sceneData.board.col
        );
        const pieces = tmpPieces.map((i: any) => {
          i.imageId = image.id;
          i.fake = image.is_fake;
          i.imageUrl = image.url;
          i.id = _.uniqueId();
          return i;
        });
        optionsData = [...optionsData, ...pieces];
      }
      return optionsData;
    };
    /**
     * 初始化答案选项
     */
    const handleInitAnswerData = () => {
      initAnswerData.value = initAnswerData.value.map((i: any) => {
        const item = sceneData.optionsData.filter(
          (j: any) =>
            j.imageId === i.image_id &&
            i.image_row === j.row &&
            i.image_col === j.col
        )[0];
        return { ...i, ...item };
      });

      initAnswerData.value.map((i: any) =>
        changeItem(i.box_col - 1, i.box_row - 1, typeMap.board.value, {
          dataId: i.id,
          type: typeMap.option.value,
          x: i.col - 1,
          y: i.row - 1,
        })
      );
    };

    const onDragStart = (e: any) => {
      handleDragStart(e.target);
    };
    const onDrop = (e: any) => {
      const element = e.target;
      handleDrop(element);
    };
    const onDragover = (event: any) => {
      event.preventDefault();
    };
    const onTouchStart = (e: any) => {
      const element: any = e.target;
      const dataId = parseInt(element.getAttribute("dataId"));
      if (isPreview.value || !dataId) {
        // e.preventDefault();
        return;
      }
      if (e.cancelable) {
        e.preventDefault();
      }
      console.log("touchstart");
      draggingImgData.url = element.getAttribute("src");
      draggingImgData.height = element.height;
      draggingImgData.width = element.width;
      handleDragStart(element);
      const sourceTouch = e.changedTouches[0];
      draggingImgData.style = {
        top: `${sourceTouch.clientY}px`,
        left: `${sourceTouch.clientX}px`,
      };
      draggingImgData.show = true;
    };
    const onTouchEnd = (e: any) => {
      const sourceEl = e.changedTouches[0];
      if (!draggingImgData.show) {
        return;
      }
      clearDraggingImg();
      setTimeout(() => {
        const el = document.elementFromPoint(
          sourceEl.clientX,
          sourceEl.clientY
        );
        handleDrop(el);
      });
    };
    const onTouchCancel = (e: any) => {
      console.log("touchcancel", e);
      activeItem.value = {};
      draggingImgData.show = false;
    };
    const onTouchMove = _.throttle((e: any) => {
      const sourceTouch = e.changedTouches[0];
      if (!draggingImgData.show) {
        return;
      }
      if (e.cancelable) {
        e.preventDefault();
      }
      const style = {
        top: `${sourceTouch.clientY}px`,
        left: `${sourceTouch.clientX}px`,
      };
      draggingImgData.style = style;
    }, 20);
    /**
     * 初始化所有数据
     */
    const initData = async () => {
      // 分割处理数据
      try {
        sceneData.optionsData = questionData.value.question_params.image_items;
        const imageItems = await splitImage();
        sceneData.optionsData = sceneData.optionsData.map((i: any) => {
          return imageItems.filter(
            (j: any) =>
              j.imageId === i.image_id && i.row === j.row && i.col === j.col
          )[0];
        });

        sceneData.board = {
          row: questionData.value.question_params.row_num,
          col: questionData.value.question_params.col_num,
        };
        boardData.value = new Array(sceneData.board.row)
          .fill({})
          .map(() =>
            new Array(sceneData.board.col)
              .fill({})
              .map(() => _.cloneDeep(boardItem))
          );

        handleInitAnswerData();
        initLoading.value = false;
      } catch (error) {
        initLoading.value = false;
      }

      sceneRef.value.addEventListener("dragstart", onDragStart);
      sceneRef.value.addEventListener("drop", onDrop);
      sceneRef.value.addEventListener("dragover", onDragover);
      sceneRef.value.addEventListener("touchstart", onTouchStart);
      sceneRef.value.addEventListener("touchend", onTouchEnd);
      sceneRef.value.addEventListener("touchcancel", onTouchCancel);
      // 禁止页面滑动
      document.body.style.overflow = "hidden";
      sceneRef.value.addEventListener("touchmove", onTouchMove);
    };

    const getAnswer = () => {
      if (initLoading.value) {
        return { type: 0 };
      }
      const answerParams = {
        items: answerData.value.map((i: any) => {
          return {
            image_id: i.imageId,
            box_row: i.y + 1,
            box_col: i.x + 1,
            image_row: i.row,
            image_col: i.col,
          };
        }),
      };
      if (!questionData.value.answer_params) {
        //* 没有回答过答案
        if (!answerData.value.length) {
          return { type: 0 };
        }
        return {
          type: 1,
          answer_params: answerParams,
        };
      } else {
        //* 回答过答案
        if (
          JSON.stringify(questionData.value.answer_params) ===
          JSON.stringify(answerParams)
        ) {
          //*  跟之前答过的一样
          return { type: 0 };
        }
        return {
          type: 1,
          answer_params:
            answerData.value.length === 0 ? undefined : answerParams,
        };
      }
    };
    onMounted(async () => {
      // loading.value = ElLoading.service({
      //   lock: true,
      //   text: "处理中...",
      //   background: "rgba(0, 0, 0, 0.3)",
      // });
      jigSawLoading.value = true;
      await initData();
      unLock();
      // loading.value && loading.value.close();
      jigSawLoading.value = false;
    });

    onBeforeUnmount(() => {
      sceneRef.value.removeEventListener("touchmove", onTouchMove);
      sceneRef.value.removeEventListener("touchend", onTouchEnd);
      sceneRef.value.removeEventListener("touchstart", onTouchStart);
      sceneRef.value.removeEventListener("touchcancel", onTouchCancel);
      sceneRef.value.removeEventListener("dragstart", onDragStart);
      sceneRef.value.removeEventListener("dragover", onDragover);
      sceneRef.value.removeEventListener("drop", onDrop);
    });

    return {
      sceneRef,
      questionTitle,
      initLoading,
      answerImgSrc,
      boardRef,
      questionData,
      props,
      draggingImgData,
      isPreview,
      sceneData,
      boardData,
      typeMap,
      getAnswer,
      getBoardStyle,
      getAspectRatioBoardStyle,
      getJigsawWidth,
      jigSawLoading,
      jigSawText,
    };
  },
});
